import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Ear, Eye, Hand, Users, Clock, Brain, UserX, Smile } from 'lucide-react';
import { NunchiRule } from '../types';

const RULES: NunchiRule[] = [
  {
    id: 1,
    title: "Empty Your Mind",
    korean: "마음을 비우세요",
    description: "To read the room, you must first stop projecting your own thoughts. Listen twice as much as you speak. Your assumptions are the enemy of observation.",
    icon: "Brain"
  },
  {
    id: 2,
    title: "The Room is a Living Thing",
    korean: "분위기 파악",
    description: "The atmosphere changed the moment you entered. Be aware of your own influence. A room has a 'temperature'—is it cold, warm, tense, or relaxed?",
    icon: "Users"
  },
  {
    id: 3,
    title: "Watch the Eyes",
    korean: "눈치",
    description: "People say one thing but mean another. Their eyes, posture, and tone tell the real truth. Watch who people look at when they laugh—that's the hierarchy.",
    icon: "Eye"
  },
  {
    id: 4,
    title: "Nunchi Must Be Fast",
    korean: "눈치 빠르다",
    description: "A slow Nunchi is no Nunchi. The moment passes quickly. You must process social data in real-time to avoid awkwardness.",
    icon: "Clock"
  },
  {
    id: 5,
    title: "Manners Exist for a Reason",
    korean: "예의",
    description: "Standard protocols make people feel safe. Knowing when to bow, shake hands, or wait to eat puts others at ease.",
    icon: "Hand"
  },
  {
    id: 6,
    title: "Read the Silence",
    korean: "침묵을 읽다",
    description: "What isn't being said is often louder than what is. An uncomfortable silence is a loud alarm bell for a Nunchi master.",
    icon: "Ear"
  },
  {
    id: 7,
    title: "Don't Cause Trouble",
    korean: "폐 끼치지 마라",
    description: "The goal is harmony. Standing out too much, positively or negatively, can disrupt the flow. Blend in before you try to stand out.",
    icon: "UserX"
  },
  {
    id: 8,
    title: "It's About Survival",
    korean: "생존",
    description: "Nunchi isn't just politeness; it's a survival mechanism. It helps you avoid danger and find allies in a complex world.",
    icon: "Smile"
  }
];

const IconMap: Record<string, React.ReactNode> = {
  Brain: <Brain size={24} />,
  Users: <Users size={24} />,
  Eye: <Eye size={24} />,
  Clock: <Clock size={24} />,
  Hand: <Hand size={24} />,
  Ear: <Ear size={24} />,
  UserX: <UserX size={24} />,
  Smile: <Smile size={24} />,
};

const Learn: React.FC = () => {
  const [openId, setOpenId] = useState<number | null>(1);

  const toggle = (id: number) => {
    setOpenId(openId === id ? null : id);
  };

  return (
    <div className="max-w-3xl mx-auto px-6 pb-20">
      <header className="mb-10 text-center">
        <h1 className="text-3xl font-serif font-bold text-nunchi-900 mb-3">8 Rules of Nunchi</h1>
        <p className="text-nunchi-600">Understand the foundational pillars to sharpen your intuition.</p>
      </header>

      <div className="space-y-4">
        {RULES.map((rule) => (
          <div 
            key={rule.id} 
            className={`bg-white border rounded-xl overflow-hidden transition-all duration-300 ${openId === rule.id ? 'border-nunchi-400 shadow-md' : 'border-nunchi-200 hover:border-nunchi-300'}`}
          >
            <button 
              onClick={() => toggle(rule.id)}
              className="w-full flex items-center justify-between p-5 text-left focus:outline-none"
            >
              <div className="flex items-center gap-4">
                <div className={`p-2 rounded-lg ${openId === rule.id ? 'bg-nunchi-100 text-nunchi-700' : 'bg-gray-50 text-gray-400'}`}>
                  {IconMap[rule.icon]}
                </div>
                <div>
                  <h3 className={`font-bold text-lg ${openId === rule.id ? 'text-nunchi-900' : 'text-gray-700'}`}>
                    {rule.title}
                  </h3>
                  <p className="text-xs text-nunchi-500 font-serif">{rule.korean}</p>
                </div>
              </div>
              <div className="text-nunchi-400">
                {openId === rule.id ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
              </div>
            </button>
            
            <div className={`overflow-hidden transition-[max-height] duration-500 ease-in-out ${openId === rule.id ? 'max-h-40' : 'max-h-0'}`}>
              <div className="p-5 pt-0 text-nunchi-600 leading-relaxed border-t border-dashed border-nunchi-100 mt-2">
                {rule.description}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Learn;